import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import type { ActiveNav } from '../navigation/useNavigation'

/**
 * Sub-navegação de um módulo: um separador por rota (partilhável por link), com
 * scroll horizontal no telemóvel. Um separador que agrupa várias páginas mostra
 * por baixo uma escolha segmentada entre elas.
 */
export default function ModuleTabs({ active }: { active: ActiveNav }) {
  const { t } = useTranslation()
  const { module, tab, leaf } = active
  const subLeaves = tab.leaves.filter((l) => !l.hiddenInNav)
  const showTabs = module.tabs.length > 1
  const showSub = subLeaves.length > 1
  if (!showTabs && !showSub) return null

  return (
    <Box component="nav" aria-label={t(module.label)} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
      {showTabs && (
        <Tabs
          value={tab.id}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={t(module.label)}
          sx={{
            px: { xs: 1, sm: 2 },
            minHeight: 48,
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'brand.teal500' },
            '& .MuiTab-root': { minHeight: 48, px: 2, color: 'text.secondary' },
            '& .MuiTab-root.Mui-selected': { color: 'primary.main' },
          }}
        >
          {module.tabs.map((mt) => (
            <Tab key={mt.id} value={mt.id} label={t(mt.label)} component={RouterLink} to={mt.href} />
          ))}
        </Tabs>
      )}
      {showSub && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.25, overflowX: 'auto', borderTop: showTabs ? '1px solid' : 'none', borderColor: 'divider' }}>
          <ToggleButtonGroup size="small" value={leaf.id} exclusive aria-label={t(tab.label)}>
            {subLeaves.map((l) => (
              <ToggleButton
                key={l.id}
                value={l.id}
                component={RouterLink}
                to={l.href}
                sx={{ px: 2, whiteSpace: 'nowrap', '&.Mui-selected': { bgcolor: 'brand.teal50', color: 'primary.main', '&:hover': { bgcolor: 'brand.teal50' } } }}
              >
                {t(l.label)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  )
}
